import React from "react";
import { BookOpen, FileText, Award, Users } from "lucide-react";

const Research = () => {
  const publications = [
    {
      title: "Landfill Detection Using Computer Vision and Aerial Imagery",
      conference: "IEEE ISCV Conference",
      date: "May 2024",
      description: "Novel approach to automatically detect and classify landfill sites from aerial imagery using deep learning techniques and computer vision algorithms.",
      link: "#"
    },
  
  ];

  const researchInterests = [
    "Computer Vision and Deep Learning",
    "Autonomous Systems",
    "Multi-modal Learning",
    "MLOps and Model Deployment",
    "Edge AI Computing"
  ];

  const advisor = {
    name: "Dr. Lokesh Das",
    title: "Professor of Computer Science",
    institution: "Wichita State University",
    department: "Department of Electrical Engineering and Computer Science",
    researchFocus: "Reinforcement Learning and Autonomous Vehicles",
    link: "#"
  };

  return (
    <section id="research" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-800 mb-4">Research</h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Currently pursuing a PhD in Computer Science at Wichita State University, focusing on 
            computer vision and machine learning applications for autonomous systems.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Research Interests */}
          <div className="bg-white rounded-xl p-8 shadow-md">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <BookOpen className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 ml-4">Research Interests</h3>
            </div>
            
            <ul className="space-y-3">
              {researchInterests.map((interest, index) => (
                <li key={index} className="flex items-center">
                  <div className="w-2 h-2 bg-indigo-600 rounded-full mr-3"></div>
                  <span className="text-gray-700">{interest}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Advisor Information */}
          <div className="bg-white rounded-xl p-8 shadow-md">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Users className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 ml-4">Advisor</h3>
            </div>
            
            <div className="text-center mb-6">
              <div className="w-24 h-24 bg-indigo-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="text-3xl font-bold text-indigo-600">{advisor.name.split(' ').map(name => name[0]).join('')}</span>
              </div>
              <h4 className="text-xl font-bold text-gray-800">{advisor.name}</h4>
              <p className="text-gray-600">{advisor.title}</p>
              <p className="text-gray-600 text-sm">{advisor.institution}</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <h5 className="text-sm font-medium text-gray-500 uppercase">Department</h5>
                <p className="text-gray-700">{advisor.department}</p>
              </div>
              <div>
                <h5 className="text-sm font-medium text-gray-500 uppercase">Research Focus</h5>
                <p className="text-gray-700">{advisor.researchFocus}</p>
              </div>
            </div>
            
            <div className="mt-6">
              <a 
                href={advisor.link}
                className="block w-full py-2 px-4 bg-indigo-50 text-indigo-600 font-medium text-center rounded-lg hover:bg-indigo-100 transition-colors"
              >
                View Profile
              </a>
            </div>
          </div>

          {/* Publications */}
          <div className="bg-white rounded-xl p-8 shadow-md lg:row-span-2">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <FileText className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 ml-4">Publications</h3>
            </div>
            
            <div className="space-y-8">
              {publications.map((publication, index) => (
                <div key={index} className="border-l-2 border-indigo-200 pl-4">
                  <span className="text-sm font-medium text-indigo-600 block">{publication.date}</span>
                  <h4 className="text-lg font-bold text-gray-800 mt-1 hover:text-indigo-600 transition-colors">
                    <a href={publication.link}>{publication.title}</a>
                  </h4>
                  <p className="text-gray-600 text-sm">{publication.conference}</p>
                  <p className="text-gray-600 mt-2 text-sm">{publication.description}</p>
                </div>
              ))}
            </div>
            
            <div className="mt-8 text-center">
              <a 
                href="#"
                className="inline-flex items-center text-indigo-600 font-medium hover:text-indigo-800 transition-colors"
              >
                View all publications
                <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Research Highlights */}
          <div className="bg-white rounded-xl p-8 shadow-md lg:col-span-2">
            <div className="flex items-center mb-6">
              <div className="p-3 bg-indigo-100 rounded-lg">
                <Award className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 ml-4">Research Highlights</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Computer Vision</h4>
                <p className="text-gray-600 text-sm">
                  Exploring advanced deep learning architectures for image segmentation, object detection, and scene understanding in complex environments.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Autonomous Systems</h4>
                <p className="text-gray-600 text-sm">
                  Developing intelligent decision-making systems for autonomous vehicles and robots using reinforcement learning and multi-agent coordination.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Edge AI</h4>
                <p className="text-gray-600 text-sm">
                  Implementing efficient neural network architectures optimized for deployment on resource-constrained edge devices.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h4 className="text-lg font-bold text-gray-800 mb-2">Multi-modal Learning</h4>
                <p className="text-gray-600 text-sm">
                  Investigating fusion techniques for combining data from multiple sensors (RGB, depth, LiDAR) to enhance perception systems.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Research;